import { useState } from 'react';
import FunctionalUnitCascadeModal from './FunctionalUnitCascadeModal';

/**
 * Détection + application de la cascade d'unité fonctionnelle pour une section ou une
 * pièce : si des pièces/usages en dessous ont déjà une valeur différente de l'ancienne,
 * propose de la propager plutôt que de la changer silencieusement à un seul niveau.
 * Partagé entre les popups d'édition (AuditPageCreationSectionsAndRooms.js) et la vue
 * "Réorganiser" (ReorganizeView.js) pour ne pas dupliquer ce flux une troisième fois.
 */
export function useFunctionalUnitCascade() {
  const [prompt, setPrompt] = useState(null); // { targetLabel, rooms, usages, apply }

  const checkAndApply = async ({ scope, entityId, oldValue, newValue, targetLabel, apply }) => {
    const normalizedOld = oldValue || '';
    const normalizedNew = newValue || '';
    if (normalizedOld === normalizedNew) return;

    try {
      const endpoint = scope === 'section'
        ? `get_section_functional_unit_breakdown/${entityId}`
        : `get_room_functional_unit_breakdown/${entityId}`;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`);
      if (response.ok) {
        const breakdown = await response.json();
        const rooms = (breakdown.rooms || []).map((r) => ({
          _id: r._id, label: r.label, matchesOld: (r.functional_unit || '') === normalizedOld,
        }));
        const usages = (breakdown.usages || []).map((u) => ({
          _id: u._id, label: u.label, matchesOld: (u.functional_unit || '') === normalizedOld,
        }));
        if (rooms.length + usages.length > 0) {
          setPrompt({ targetLabel, rooms, usages, apply });
          return;
        }
      }
    } catch (error) {
      console.error("Erreur API :", error);
      // On ne bloque pas le changement si la vérification échoue
    }

    await apply(null);
  };

  const cascadeModal = prompt && (
    <FunctionalUnitCascadeModal
      title="Propager le changement d'unité fonctionnelle ?"
      description="Des pièces et/ou usages en dessous ont déjà une unité fonctionnelle différente. Choisissez ceux à faire suivre (les autres restent inchangés)."
      groups={[
        { key: 'rooms', label: 'Pièces', items: prompt.rooms },
        { key: 'usages', label: 'Usages', items: prompt.usages },
      ]}
      fixedTargetLabel={prompt.targetLabel}
      defaultAllChecked={false}
      onConfirm={(selection) => {
        prompt.apply({ section_ids: [], room_ids: selection.rooms, usage_ids: selection.usages });
        setPrompt(null);
      }}
      onSkip={() => { prompt.apply(null); setPrompt(null); }}
      onCancel={() => setPrompt(null)}
    />
  );

  return { checkAndApply, cascadeModal };
}
